'use client'

import { Children, type ComponentProps, useRef } from 'react'
import { clsx } from 'clsx'
import { useInView } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { ChevronIcon } from 'ui/src/icons/Chevron'
import { navButton, navButtonLeft, navButtonRight, root, slideContent, slideItem, track } from './Slideshow.css'

export type SlideshowProps = ComponentProps<'div'>

export function Slideshow({ children, className, ...rest }: SlideshowProps) {
  const { t } = useTranslation('common')
  const trackRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLDivElement>(null)
  const lastItemRef = useRef<HTMLDivElement>(null)
  const isFirstItemVisible = useInView(firstItemRef, { root: trackRef, amount: 'all' })
  const isLastItemVisible = useInView(lastItemRef, { root: trackRef, amount: 'all' })

  function scrollItems(direction: 1 | -1) {
    const trackElement = trackRef.current
    if (!trackElement) return
    // One slide is 86% of the track width (the rest is split into 7% peek gutters on each side),
    // so scrolling by that amount advances the carousel by exactly one slide.
    const distance = trackElement.clientWidth * 0.86 * direction
    trackElement.scrollBy({ left: distance, behavior: 'smooth' })
  }

  const childrenArray = Children.toArray(children)
  const lastIndex = childrenArray.length - 1

  return (
    <div className={clsx(root, className)} {...rest}>
      <div ref={trackRef} className={track}>
        {childrenArray.map((child, index) => {
          const isFirst = index === 0
          const isLast = index === lastIndex
          return (
            <div
              key={index}
              ref={(node) => {
                // Same element is assigned to both refs when there's a single slide,
                // which makes both visibility checks resolve and hides both buttons.
                if (isFirst) firstItemRef.current = node
                if (isLast) lastItemRef.current = node
              }}
              className={slideItem}
            >
              <div className={slideContent}>{child}</div>
            </div>
          )
        })}
      </div>

      {!isFirstItemVisible && (
        <button
          type="button"
          aria-label={t('SCROLL_LEFT')}
          className={clsx(navButton, navButtonLeft)}
          onClick={() => scrollItems(-1)}
        >
          <ChevronIcon direction="left" size="1.25rem" />
        </button>
      )}
      {!isLastItemVisible && (
        <button
          type="button"
          aria-label={t('SCROLL_RIGHT')}
          className={clsx(navButton, navButtonRight)}
          onClick={() => scrollItems(1)}
        >
          <ChevronIcon direction="right" size="1.25rem" />
        </button>
      )}
    </div>
  )
}
